/**
 * Typed API fetch layer — the single gateway between the app and the backend.
 *
 * All requests go through `authFetch` (auto-refreshes the Keycloak JWT),
 * then through here for JSON parsing, `.data` unwrapping, and error normalisation.
 *
 * Usage in query hooks:
 *   queryFn: () => apiFetch<Connector[]>(`${API_BASE}/v1/connectors`)
 *
 * Usage for mutations:
 *   mutationFn: (body) => apiFetch(`${API_BASE}/v1/alerts`, post(body))
 */

import { apiErrorMessage } from "./api-error-message";
import { authFetch, AuthRefreshFailed } from "./fetch";
import { captureError, scrubUrl } from "./posthog";
import { getDiagSnapshot } from "./diag-capture";
import { isUnreported, shouldReportNetworkFailure, unreportedInit } from "./report-gate";

// ─── Error type ───────────────────────────────────────────────────────────────

export class ApiError extends Error {
  public readonly errorCategory?: string;
  public readonly errorSummary?: string;
  public readonly fixSuggestion?: string;
  public readonly fixAction?: Record<string, unknown>;
  public readonly executionId?: string;
  /** The parsed error body, when there was one. Kept because callers that
   *  handle a status themselves (a 404 that means "not generated yet")
   *  otherwise lose every field the endpoint sent to explain it. */
  public readonly body?: Record<string, unknown>;

  constructor(
    public readonly status: number,
    message: string,
    extra?: { errorCategory?: string; errorSummary?: string; fixSuggestion?: string; fixAction?: Record<string, unknown>; executionId?: string; body?: Record<string, unknown> },
  ) {
    super(message);
    this.name = "ApiError";
    this.errorCategory = extra?.errorCategory;
    this.errorSummary = extra?.errorSummary;
    this.fixSuggestion = extra?.fixSuggestion;
    this.fixAction = extra?.fixAction;
    this.executionId = extra?.executionId;
    this.body = extra?.body;
  }
}

// ─── Retry helper for transient network errors ──────────────────────────────
// During blue-green deployments, the GKE NEG takes 5-30s to deregister old pods.
// Requests hitting a deregistered pod get TCP RST → "Failed to fetch" in the browser.
// Retrying safe methods (GET/HEAD) covers this window transparently.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError && (err.message === "Failed to fetch" || err.message === "NetworkError when attempting to fetch resource.")) return true;
  if (err instanceof DOMException && err.name === "AbortError") return false;
  return false;
}

// How long the last retry waits for the browser to come back online. A connection
// that drops for longer than the 6 s the retries span otherwise fails every request
// it took down, though the same request succeeds the moment the network returns.
const ONLINE_WAIT_MS = 15_000;

const idempotentInits = new WeakSet<object>();

function isRetryable(init: RequestInit | undefined): boolean {
  const method = (init?.method ?? "GET").toUpperCase();
  return method === "GET" || method === "HEAD" || (!!init && idempotentInits.has(init));
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function waitForOnline(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (!isOffline() || typeof window === "undefined") return resolve();
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener("online", done);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    window.addEventListener("online", done);
  });
}

async function fetchWithRetry(url: string, init: RequestInit | undefined, attempt = 0): Promise<Response> {
  try {
    return await authFetch(url, init);
  } catch (err) {
    if (isNetworkError(err) && isRetryable(init) && attempt < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
      if (attempt === MAX_RETRIES - 1) await waitForOnline(ONLINE_WAIT_MS);
      return fetchWithRetry(url, init, attempt + 1);
    }
    // Reported here rather than at the call sites because this is where a
    // network failure becomes final: the retries above cover the blue-green
    // deregistration window, so anything reaching this line survived them and
    // is a request the user genuinely lost. Reporting before the retry loop
    // would turn every deploy into an incident.
    // AuthRefreshFailed is Keycloak being unreachable, not this request failing: during
    // an outage every polling query in every tab raises it until the refresh succeeds.
    // SessionExpired is a link session's static token (20 minutes) running out: the
    // review pages render it as an expired link, which is the expected end of every
    // link left open. Matched by name because many tests stub ./fetch without it.
    if (
      err instanceof Error
      && !(err instanceof AuthRefreshFailed)
      && err.name !== "SessionExpired"
      && !isUnreported(init)
      && (!(err instanceof TypeError) || shouldReportNetworkFailure("exception"))
    ) reportApiFailure(err, url, init);
    throw err;
  }
}

// ─── Error reporting ──────────────────────────────────────────────────────────

/**
 * Which failures are worth an Error Tracking issue.
 *
 * 5xx and network failures only. A 4xx is almost always the product working:
 * a 404 for a report not generated yet, a 403 the RoleGate expects, a 422 the
 * form is about to render as field errors. Reporting those would bury the
 * server faults under an order of magnitude of normal traffic, and an issue
 * list nobody trusts is one nobody reads.
 *
 * 401 is excluded for the same reason AND a stronger one: authFetch refreshes
 * and retries on 401, so the first one is a routine step of the auth flow.
 */
function isReportableStatus(status: number): boolean {
  return status >= 500;
}

/**
 * The last few network calls, trimmed for an event payload.
 *
 * Request LINES only -- method, scrubbed URL, status, duration, request id.
 * Bodies are dropped: this product moves the partner's customers' business
 * data, and the request line is what says what broke.
 *
 * Console messages are deliberately NOT included, though the buffer holds them.
 * `diag-capture` formats console arguments by JSON.stringifying whatever was
 * passed, so a single `console.error("failed for", customer)` puts an entire
 * customer record in the buffer. The credential scrub does not help: it matches
 * token SHAPES, and a name, an email address or an order is not a shape. There
 * is no sanitizer that makes arbitrary free-form output safe to export.
 *
 * The buffer itself is fine where it came from -- the bug-report widget, where
 * a person reads what they are sending and chooses to send it. Attaching it to
 * automatic telemetry is a different consent model, and it would have undone
 * the privacy work in the rest of this change.
 */
function diagContext(): Record<string, unknown> {
  try {
    const snap = getDiagSnapshot();
    return {
      recent_requests: snap.network.slice(-5).map((c) => ({
        method: c.method,
        url: scrubUrl(c.url),
        status: c.status,
        duration_ms: c.durationMs,
        request_id: c.requestId,
      })),
      // Count only. Enough to tell "the app was already unhappy" from "this
      // failed out of nowhere", without exporting a word of what was logged.
      recent_console_error_count: snap.consoleLog.length,
      build_sha: snap.browser.buildSha,
    };
  } catch {
    // The snapshot is a nicety; never let it stop the report itself.
    return {};
  }
}

/** Report a failed API call, with the request that failed and what preceded it. */
function reportApiFailure(err: ApiError | Error, url: string, init: RequestInit | undefined, requestId?: string | null): void {
  captureError(err, {
    // scrubUrl strips OAuth material and rewrites share-link path segments, the
    // same treatment $current_url gets -- an unredacted URL here would reopen
    // exactly the hole that scrubbing closed everywhere else.
    api_url: scrubUrl(url),
    api_method: (init?.method ?? "GET").toUpperCase(),
    http_status: err instanceof ApiError ? err.status : null,
    error_category: err instanceof ApiError ? err.errorCategory ?? null : null,
    request_id: requestId ?? null,
    ...diagContext(),
  });
}

// ─── Error normalisation ──────────────────────────────────────────────────────

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Build the ApiError for a non-2xx response.
 *
 * The message comes from `apiErrorMessage`, which renders the field-level
 * reasons from a validation envelope's `details` instead of echoing its `error`. An
 * unparseable body (an HTML error page from a proxy, an empty 502) leaves it
 * with only the status, which is what it reports.
 */
async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // response has no JSON body — the status is all we have
  }
  const fields = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  return new ApiError(res.status, apiErrorMessage(body, res.status), {
    errorCategory: optionalString(fields.errorCategory),
    errorSummary: optionalString(fields.errorSummary),
    fixSuggestion: optionalString(fields.fixSuggestion),
    fixAction: fields.fixAction && typeof fields.fixAction === "object"
      ? (fields.fixAction as Record<string, unknown>)
      : undefined,
    executionId: optionalString(fields.executionId),
    // The inline version this helper replaced passed the parsed body, and
    // use-workflows.ts reads err.body?.lastGeneration off it -- extracting
    // without this silently drops that path.
    body: body && typeof body === "object" ? (body as Record<string, unknown>) : undefined,
  });
}

/**
 * Normalise a failed response AND report it when it is worth an issue.
 *
 * Both fetchers call this rather than toApiError directly, so a server fault
 * cannot be reported on one path and silently swallowed on the other.
 */
async function reportedApiError(res: Response, url: string, init: RequestInit | undefined): Promise<ApiError> {
  const err = await toApiError(res);
  if (isReportableStatus(res.status) && !isUnreported(init)) {
    // x-request-id is what ties this to the server log; the gateway sets it on
    // every response and diag-capture already reads it for the bug report.
    reportApiFailure(err, url, init, res.headers.get("x-request-id"));
  }
  return err;
}

// ─── Core fetcher ─────────────────────────────────────────────────────────────

export interface ApiFetchOptions {
  /**
   * false when a failure is an expected answer, not a fault (a probe asking
   * whether a customer's link opens). The error is still thrown; it is only
   * kept out of Error Tracking and api_request_failed.
   */
  reportFailure?: boolean;
  /**
   * true when sending the request twice is harmless (the server treats a repeat
   * as a no-op, or the call only reads), so a network failure is retried like a
   * GET. Never set it on a create: a request that reached the server before the
   * connection dropped would run twice.
   */
  idempotent?: boolean;
}

function withReportPolicy(init: RequestInit | undefined, opts: ApiFetchOptions | undefined): RequestInit | undefined {
  const policed = opts?.reportFailure === false ? unreportedInit(init) : init;
  if (!opts?.idempotent) return policed;
  const copy: RequestInit = policed && policed !== init ? policed : { ...init };
  idempotentInits.add(copy);
  return copy;
}

/**
 * Authenticated fetch with automatic JSON parsing and `.data` unwrapping.
 * Retries up to MAX_RETRIES (3) times on network errors (GET/HEAD, or a request
 * marked `idempotent`) to handle brief unavailability during blue-green deployments
 * and short connection drops; while the browser reports itself offline the last
 * retry waits up to ONLINE_WAIT_MS for it to come back.
 * Throws `ApiError` on non-2xx responses — React Query catches these automatically.
 */
export async function apiFetch<T>(url: string, init?: RequestInit, opts?: ApiFetchOptions): Promise<T> {
  init = withReportPolicy(init, opts);
  const res = await fetchWithRetry(url, init);

  if (!res.ok) throw await reportedApiError(res, url, init);

  const json = (await res.json()) as { data?: T } | T;
  return ((json as { data?: T }).data ?? json) as T;
}

/**
 * Like `apiFetch` but returns the full JSON body without `.data` unwrapping.
 * Use for paginated endpoints that return `{ data, nextCursor, total }`.
 */
export async function apiFetchRaw<T>(url: string, init?: RequestInit, opts?: ApiFetchOptions): Promise<T> {
  init = withReportPolicy(init, opts);
  const res = await fetchWithRetry(url, init);

  if (!res.ok) throw await reportedApiError(res, url, init);

  return (await res.json()) as T;
}

// ─── RequestInit helpers ──────────────────────────────────────────────────────

/** Builds a POST / PUT / PATCH RequestInit with JSON body. */
export function post(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function put(body: unknown): RequestInit {
  return {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export const del: RequestInit = { method: "DELETE" };
